(() => {
  const ready = (callback) => {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', callback, { once: true });
    else callback();
  };

  ready(() => {
    // Attribution starts before this script. Wait for its first cart sync so
    // the order attributes survive automatic cart preloading and Buy Now.
    const waitForAttribution = () => window.JohgeeAttribution?.ready || Promise.resolve();
    const autoCartVariant = document.body.dataset.autoCartVariant;
    const autoCartQuantity = Number(document.body.dataset.autoCartQuantity || 1);
    const shopRoot = window.Shopify?.routes?.root || '/';
    const experimentKey = 'johgee_cart_preload_experiment_v1';
    const experimentAttribute = 'Experiment · Cart preload';
    const experimentVersion = '2026-08-10';
    const experimentMaxAge = 30 * 24 * 60 * 60 * 1000;
    const isUkStorefront = /^(www\.)?johgeeirrig\.co\.uk$/i.test(window.location.hostname);

    // This controlled test is limited to the UK. Each visitor keeps the same
    // group for 30 days, so the cart behaviour cannot switch mid-journey.
    const chooseExperimentGroup = () => {
      if (!isUkStorefront) return 'A';
      try {
        const saved = JSON.parse(window.localStorage.getItem(experimentKey) || 'null');
        if (saved && (saved.group === 'A' || saved.group === 'B') &&
            Date.now() - Number(saved.assignedAt || 0) < experimentMaxAge) return saved.group;
        const random = window.crypto?.getRandomValues
          ? window.crypto.getRandomValues(new Uint32Array(1))[0] / 0x100000000
          : Math.random();
        const group = random < 0.5 ? 'A' : 'B';
        window.localStorage.setItem(experimentKey, JSON.stringify({ group, assignedAt: Date.now(), version: experimentVersion }));
        return group;
      } catch (_) {
        return Math.random() < 0.5 ? 'A' : 'B';
      }
    };

    const experimentGroup = chooseExperimentGroup();
    const preloadEnabled = Boolean(autoCartVariant) && (!isUkStorefront || experimentGroup === 'A');
    document.documentElement.dataset.cartPreloadExperiment = experimentGroup;
    window.JohgeeCartPreloadExperiment = { group: experimentGroup, version: experimentVersion, enabled: isUkStorefront };

    // Write the group only once a cart has an item. This avoids creating a cart
    // for a reader, while every eventual UK order retains its experiment group.
    const syncExperimentToCart = async (knownCart) => {
      const cart = knownCart || await fetch(`${shopRoot}cart.js`, {
        headers: { Accept: 'application/json' }, credentials: 'same-origin'
      }).then((response) => response.json());
      if (!cart || !cart.item_count || !isUkStorefront) return cart;
      const attributes = cart.attributes || {};
      const value = `${experimentGroup}_${experimentVersion}`;
      if (attributes[experimentAttribute] === value) return cart;
      const response = await fetch(`${shopRoot}cart/update.js`, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { ...attributes, [experimentAttribute]: value } })
      });
      if (!response.ok) throw new Error('Unable to save experiment group');
      return response.json();
    };
    const markAutoCartReady = (quantity) => {
      document.body.dataset.autoCartReady = 'true';
      document.querySelectorAll('.market-nav a[href="/cart"]').forEach((link) => {
        link.textContent = link.textContent.replace(/\(\d+\)\s*$/, `(${quantity})`);
      });
      document.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
        const variantInput = form.querySelector('[name="id"]');
        const submit = form.querySelector('button[type="submit"]');
        if (!variantInput || !submit || String(variantInput.value) !== String(autoCartVariant)) return;
        const label = submit.dataset.cartReadyLabel;
        if (label) {
          const span = submit.querySelector('span');
          if (span) span.textContent = label;
          else submit.textContent = label;
        }
      });
    };

    document.addEventListener('submit', (event) => {
      const form = event.target.closest?.('form[action*="/cart/add"]');
      if (!form || document.body.dataset.autoCartReady !== 'true') return;
      const variantInput = form.querySelector('[name="id"]');
      if (!variantInput || String(variantInput.value) !== String(autoCartVariant)) return;
      event.preventDefault();
      window.location.assign(`${shopRoot}cart`);
    }, true);

    if (preloadEnabled) {
      const preloadCart = async () => {
        await waitForAttribution();
        const cartResponse = await fetch(`${shopRoot}cart.js`, { headers: { Accept: 'application/json' } });
        if (!cartResponse.ok) throw new Error('Unable to read cart');
        const cart = await cartResponse.json();
        if (cart.item_count > 0) {
          const currentLine = cart.items.find((item) => String(item.variant_id) === String(autoCartVariant));
          if (currentLine) markAutoCartReady(currentLine.quantity);
          await syncExperimentToCart(cart);
          return;
        }
        const addResponse = await fetch(`${shopRoot}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(autoCartVariant), quantity: autoCartQuantity }] })
        });
        if (!addResponse.ok) throw new Error('Unable to preload cart');
        await addResponse.json();
        const addedCart = await fetch(`${shopRoot}cart.js`, {
          headers: { Accept: 'application/json' }, credentials: 'same-origin'
        }).then((response) => response.json());
        await syncExperimentToCart(addedCart);
        markAutoCartReady(autoCartQuantity);
        document.dispatchEvent(new CustomEvent('johgee:cart-preloaded', { detail: { quantity: autoCartQuantity } }));
      };
      const preloadPromise = navigator.locks?.request
        ? navigator.locks.request(`johgee-auto-cart-${autoCartVariant}`, preloadCart)
        : preloadCart();
      preloadPromise.catch(() => {
          // The product can be unavailable in a preview/default market. Keep
          // the storefront usable and let the explicit add-to-cart form work.
        });
    } else if (isUkStorefront) {
      // A B-group visitor with an existing intentional cart still receives a
      // group label; no product is added automatically here.
      syncExperimentToCart().catch(() => {});
    }

    // The UK product page is intentionally rendered from the archived
    // storefront markup. Keep its original button placement and gallery UI,
    // but connect those controls to Shopify's cart and checkout.
    const ensureCurrentProductInCart = async () => {
      if (!autoCartVariant) return null;
      await waitForAttribution();
      const readCart = async () => fetch(`${shopRoot}cart.js`, { headers: { Accept: 'application/json' } }).then((response) => response.json());
      let cart = await readCart();
      if (!cart.items.some((item) => String(item.variant_id) === String(autoCartVariant))) {
        const response = await fetch(`${shopRoot}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(autoCartVariant), quantity: autoCartQuantity }] })
        });
        if (!response.ok) throw new Error('Unable to add product');
        cart = await readCart();
      }
      await syncExperimentToCart(cart);
      const line = cart.items.find((item) => String(item.variant_id) === String(autoCartVariant));
      if (line) markAutoCartReady(line.quantity);
      return cart;
    };

    document.querySelectorAll('[data-shopify-product-action]').forEach((button) => {
      button.addEventListener('click', async (event) => {
        event.preventDefault();
        button.disabled = true;
        try {
          await ensureCurrentProductInCart();
          window.location.assign(`${shopRoot}${button.dataset.shopifyProductAction === 'checkout' ? 'checkout' : 'cart'}`);
        } catch (_) {
          window.location.assign(`${shopRoot}cart`);
        }
      });
    });

    document.querySelectorAll('[data-legacy-gallery-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const hero = thumb.closest('[data-legacy-product-hero]');
        const mainImage = hero?.querySelector('[data-legacy-gallery-main]');
        if (!mainImage) return;
        mainImage.src = thumb.dataset.legacyGalleryThumb;
        const thumbnailImage = thumb.querySelector('img');
        if (thumbnailImage?.alt) mainImage.alt = thumbnailImage.alt;
        hero.querySelectorAll('[data-legacy-gallery-thumb]').forEach((item) => {
          item.classList.toggle('border-brand-600', item === thumb);
          item.classList.toggle('ring-2', item === thumb);
          item.classList.toggle('ring-brand-200', item === thumb);
          item.classList.toggle('border-transparent', item !== thumb);
        });
      });
    });

    // Legacy homepage CTAs are display-only after migration. Send every one to
    // the single Shopify product form in the fixed dock.
    const purchaseText = /add to (cart|basket)|shop now|buy now|in den warenkorb|jetzt kaufen|ajouter au panier|acheter maintenant|in winkelwagen|nu kopen|bestel nu|secure checkout/i;
    const legacyPurchaseButtons = Array.from(document.querySelectorAll('.market-source button')).filter((button) => {
      const text = `${button.textContent || ''} ${button.getAttribute('aria-label') || ''}`.trim();
      return button.matches('[data-add-to-cart],[data-action="add-basket"]') || purchaseText.test(text);
    });

    legacyPurchaseButtons.forEach((button) => {
      if (button.closest('.source-product')) return;
      button.addEventListener('click', (event) => {
        const productLink = button.closest('article,section,div')?.querySelector('a[href*="/products/"]');
        if (productLink && productLink.href.includes('rainpoint-2-zone')) {
          event.preventDefault();
          window.location.assign(productLink.href);
          return;
        }
        const dockButton = document.querySelector('.shopify-buy-dock button[type="submit"]');
        if (!dockButton || dockButton.disabled) return;
        event.preventDefault();
        dockButton.click();
      });
    });

    document.querySelectorAll('.market-source button[aria-label*="cart" i],.market-source button[aria-label*="panier" i],.market-source button[aria-label*="warenkorb" i]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        window.location.assign('/cart');
      });
    });

    document.querySelectorAll('[data-product-root]').forEach((root) => {
      const selector = root.querySelector('[data-variant-selector]');
      const price = root.querySelector('[data-product-price]');
      const submit = root.querySelector('[data-product-submit]');
      const form = submit && submit.closest('form');

      const syncVariant = () => {
        if (!selector || !submit) return;
        const option = selector.options[selector.selectedIndex];
        const available = option && option.dataset.available === 'true';
        if (price && option.dataset.price) price.textContent = option.dataset.price;
        submit.disabled = !available;
        const isReady = document.body.dataset.autoCartReady === 'true' && String(option?.value) === String(autoCartVariant);
        submit.querySelector('span').textContent = isReady ? submit.dataset.cartReadyLabel : (available ? submit.dataset.addLabel : submit.dataset.soldOutLabel);
      };

      if (selector) {
        selector.addEventListener('change', syncVariant);
        syncVariant();
      }

      if (form && submit) {
        form.addEventListener('submit', () => {
          const variantInput = form.querySelector('[name="id"]');
          if (document.body.dataset.autoCartReady === 'true' && String(variantInput?.value) === String(autoCartVariant)) return;
          submit.setAttribute('aria-busy', 'true');
          submit.classList.add('is-loading');
        });
      }
    });

    // The three application-based source sites used JS accordions. Recreate
    // only their disclosure behaviour; no source commerce runtime is retained.
    document.querySelectorAll('[aria-controls^="faq-panel-"]').forEach((trigger) => {
      const panel = document.getElementById(trigger.getAttribute('aria-controls'));
      if (!panel) return;
      const expanded = trigger.getAttribute('aria-expanded') === 'true';
      panel.hidden = !expanded;
      panel.style.display = expanded ? '' : 'none';
      trigger.addEventListener('click', () => {
        const willExpand = trigger.getAttribute('aria-expanded') !== 'true';
        trigger.setAttribute('aria-expanded', String(willExpand));
        panel.hidden = !willExpand;
        panel.style.display = willExpand ? '' : 'none';
      });
    });

    // Preserve the Dutch source gallery interaction without loading its old
    // checkout/site scripts.
    document.querySelectorAll('.source-product [data-action="thumb"]').forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const gallery = thumb.closest('.pd-gallery');
        const mainImage = gallery && gallery.querySelector('.pd-main img');
        const thumbImage = thumb.querySelector('img');
        if (!mainImage || !thumbImage) return;
        mainImage.src = thumbImage.currentSrc || thumbImage.src;
        mainImage.alt = thumbImage.alt || mainImage.alt;
        gallery.querySelectorAll('[data-action="thumb"]').forEach((item) => item.classList.toggle('is-active', item === thumb));
      });
    });

    // The UAE source snapshot contained internal verification notes and a
    // retired client-side cart. Those notes are not customer-facing copy and
    // the live Shopify product controls above are the sole purchase path.
    const aeSource = document.querySelector('.market-source--ae, .source-product-wrap');
    if (aeSource && /watertimer\.ae$/i.test(window.location.hostname)) {
      aeSource.querySelectorAll('.wt-evidence-scope').forEach((note) => note.remove());
      aeSource.querySelectorAll('p, li').forEach((element) => {
        if (!/\b(unverified|ordering is paused|verification paused)\b/i.test(element.textContent || '')) return;
        element.remove();
      });
      aeSource.querySelectorAll('img[alt*="unverified" i]').forEach((image) => {
        image.alt = image.alt.replace(/;?\s*compatibility.*unverified/i, '');
      });
      aeSource.querySelectorAll('[data-add-to-cart="B0FWCPPXYF"]').forEach((button) => {
        button.removeAttribute('disabled');
        button.removeAttribute('aria-disabled');
        button.removeAttribute('title');
        button.setAttribute('aria-label', 'View RainPoint 2-Zone WiFi details');
        button.textContent = 'View details';
      });
      aeSource.querySelectorAll('h2').forEach((heading) => {
        if (/verification-paused/i.test(heading.textContent || '')) heading.textContent = 'Choose the right smart water timer';
      });
    }

    // Article bodies should not contain their own H1 because the Shopify
    // article template already renders the page title as the single H1.
    // This also protects visitors while Shopify serves an older cached body.
    document.querySelectorAll('article .rte h1, main .rte h1, .rte h1').forEach((heading) => {
      const replacement = document.createElement('h2');
      for (const attr of heading.attributes) replacement.setAttribute(attr.name, attr.value);
      replacement.innerHTML = heading.innerHTML;
      heading.replaceWith(replacement);
    });

    // Temporary storefront-visible fallback for article pages whose Shopify
    // HTML page_cache can lag behind the saved article body/translation.
    // The canonical article body and Liquid template already contain these
    // proof blocks; this only helps consumers while older cached HTML is served.
    const articleHandle = window.location.pathname.replace(/\/+$/, '').split('/').pop();
    const nlFallbackHandles = new Set([
      '4-zone-watertimer-kopen',
      'beregeningscomputer-na-regen-wanneer-pas-je-je-schema-aan',
      'druppelirrigatie-timer-wifi-buitenkraan',
      'merken-beregeningscomputers-vergelijken',
      'problemen-beregeningssysteem-oplossen',
      'rainpoi-druppelirrigatie-installeren',
      'waterbesparende-tuinirrigatie',
      'wifi-beregeningscomputer-app-instellen',
      'zelf-beregening-aanleggen-of-installateur',
      'tuin-water-geven-op-vakantie-test',
      'van-regenton-tot-buitenkraan-een-rustig-plan-voor-druppelirrigatie-in-vier-zones',
    ]);
    const deFallbackHandles = new Set([
      '4-zonen-gartenbewaesserung-planen',
      'bewaesserung-im-urlaub-testen',
      'bewasserungscomputer-mit-4-zonen-einrichten-der-erste-test-am-aussenwasserhahn',
      'gewachshaus-rasen-und-hochbeet-getrennt-bewassern-ein-4-zonen-plan-ohne-ratespiel',
      'kein-wlan-am-wasserhahn-gateway',
    ]);
    const frFallbackHandles = new Set([
      'repartir-arrosage-jardin-4-zones',
      'wifi-ou-passerelle-arrosage-jardin',
    ]);
    const shouldAddEvidence = (nlFallbackHandles.has(articleHandle) || deFallbackHandles.has(articleHandle))
      && !document.querySelector('[data-johgee-evidence-refresh]');
    const shouldAddDepth = (nlFallbackHandles.has(articleHandle) || deFallbackHandles.has(articleHandle) || frFallbackHandles.has(articleHandle))
      && !document.querySelector('[data-johgee-depth-refresh]');
    if (shouldAddEvidence || shouldAddDepth) {
      const articleRte = document.querySelector('article .rte') || document.querySelector('main article') || document.querySelector('main');
      if (articleRte) {
        const wrap = document.createElement('div');
        wrap.className = 'johgee-cache-fallback';
        wrap.style.margin = '2rem 0';
        if (shouldAddEvidence && deFallbackHandles.has(articleHandle)) {
          wrap.insertAdjacentHTML('beforeend', `
            <section class="johgee-evidence-refresh" data-johgee-evidence-refresh="2026-08-15" style="margin:2rem 0;padding:1.25rem;border:1px solid #d8e7dd;border-radius:16px;background:#f7fbf8;color:#183221">
              <h2>Öffentliche Stimmen, die diese Checkliste schärfer machen</h2>
              <p>Diese öffentlichen Signale sind keine eigenen Johgee-Kundenbewertungen. Sie helfen, echte Gartenprobleme in kurze Prüfungen vor der Automatik zu übersetzen.</p>
              <ul>
                <li><a href="https://www.reddit.com/r/GardeningUK/comments/1uw8bdi/holiday_for_a_week_in_a_heatwave/">Reddit / GardeningUK</a>: “40+ Pots to keep watered while I'm on holiday” — Töpfe brauchen eigene kurze Tests.</li>
                <li><a href="https://www.reddit.com/r/Irrigation/comments/1m1hq1m/standard_or_wifi_hose_timer/">Reddit / Irrigation</a>: “standard or WiFi hose timer?” — App-Steuerung lohnt sich nur, wenn Gateway und Funkstrecke stabil sind.</li>
                <li><a href="https://www.amazon.co.uk/dp/B0H6XLVVGJ">Amazon UK</a>: öffentliche Käufer-Snippets wie “No leaks” und “valve sticks” werden als Prüfliste genutzt: Anschluss, Ventil und jede Zone testen.</li>
              </ul>
              <p>Vergleiche Johgee öffentlich: <a href="https://www.tiktok.com/@johgeeirriguk">TikTok</a>, <a href="https://www.instagram.com/johgeeirrig/">Instagram</a>, <a href="https://www.youtube.com/@JohgeeIrrigation">YouTube</a>, <a href="https://www.youtube.com/@MichalsUKGardenLab">Michal’s UK Garden Lab</a> und <a href="https://www.facebook.com/JohgeeIrrig">Facebook</a>.</p>
            </section>`);
        } else if (shouldAddEvidence) {
          wrap.insertAdjacentHTML('beforeend', `
            <section class="johgee-evidence-refresh" data-johgee-evidence-refresh="2026-08-15" style="margin:2rem 0;padding:1.25rem;border:1px solid #d8e7dd;border-radius:16px;background:#f7fbf8;color:#183221">
              <h2>Openbare ervaringen die deze keuze minder theoretisch maken</h2>
              <p>Deze openbare signalen zijn geen eigen klantbeoordelingen van Johgee of Beregeningswinkel. Ze maken van echte zorgen een korte controlelijst.</p>
              <ul>
                <li><a href="https://www.reddit.com/r/GardeningUK/comments/13nstw2/how_do_you_water_your_containers_when_on_holiday/">Reddit / GardeningUK</a>: “slightly panicking about my hanging baskets” — hangmanden en potten drogen sneller dan borders.</li>
                <li><a href="https://www.reddit.com/r/GardeningUK/comments/1uw8bdi/holiday_for_a_week_in_a_heatwave/">Reddit / GardeningUK</a>: “40+ Pots to keep watered while I'm on holiday” — veel potten vragen om zones en korte tests.</li>
                <li><a href="https://www.amazon.co.uk/dp/B0H6XLVVGJ">Amazon UK</a>: openbare kopers-snippets zoals “No leaks” en “gateway dead out of the box” gebruik je als testlijst: koppeling, gateway en elke klep controleren.</li>
              </ul>
              <p>Controleer Johgee ook publiek: <a href="https://www.tiktok.com/@johgeeirriguk">TikTok</a>, <a href="https://www.instagram.com/johgeeirrig/">Instagram</a>, <a href="https://www.youtube.com/@JohgeeIrrigation">YouTube</a>, <a href="https://www.youtube.com/@MichalsUKGardenLab">Michal’s UK Garden Lab</a> en <a href="https://www.facebook.com/JohgeeIrrig">Facebook</a>.</p>
            </section>`);
        }
        if (shouldAddDepth) {
          if (deFallbackHandles.has(articleHandle)) {
            wrap.insertAdjacentHTML('beforeend', `
              <section data-johgee-depth-refresh="2026-08-15" style="margin:2rem 0;padding:1.25rem;border:1px solid #e4eadf;border-radius:16px;background:#fff;color:#183221">
                <h2>Zusätzlicher Praxistest vor dem Bewässerungsplan</h2>
                <p>Prüfen Sie jede Zone im echten Garten, nicht nur in der App. Starten Sie mit der längsten Schlauchstrecke, beobachten Sie den letzten Tropfer oder Sprenger und kontrollieren Sie den Anschluss am Wasserhahn mit einem trockenen Tuch. Wenn dort nach zehn Minuten noch alles trocken ist und am Ende der Leitung genug Wasser ankommt, ist die Zone zuverlässig genug für einen Zeitplan.</p>
                <p>Beschriften Sie die Ausgänge nach Gartenbereichen wie Kübel, Hochbeet, Rasenrand oder Gewächshaus. So lässt sich später gezielt eine Zone ändern, statt den gesamten Plan neu zu schreiben.</p>
              </section>`);
          } else if (frFallbackHandles.has(articleHandle)) {
            wrap.insertAdjacentHTML('beforeend', `
              <section data-johgee-depth-refresh="2026-08-15" style="margin:2rem 0;padding:1.25rem;border:1px solid #e4eadf;border-radius:16px;background:#fff;color:#183221">
                <h2>Contrôle pratique avant de laisser le programmateur travailler seul</h2>
                <p>Testez chaque voie séparément dans le jardin réel. Ouvrez la zone pendant dix minutes, regardez le raccord au robinet, puis vérifiez le dernier goutteur ou arroseur. Si le point le plus éloigné reçoit encore assez d’eau et que le raccord reste sec, le réglage est beaucoup plus fiable pour une semaine chaude ou une absence.</p>
                <p>Donnez aussi un nom clair à chaque sortie: pots au soleil, potager, pelouse ou massif ombragé. Quand une plante sèche trop vite, vous saurez exactement quelle zone ajuster.</p>
              </section>`);
          } else {
            wrap.insertAdjacentHTML('beforeend', `
              <section data-johgee-depth-refresh="2026-08-15" style="margin:2rem 0;padding:1.25rem;border:1px solid #e4eadf;border-radius:16px;background:#fff;color:#183221">
                <h2>Extra praktijkcontrole voor Nederlandse tuinen</h2>
                <p>Maak vóór aankoop of installatie een eenvoudige tuinkaart: potten en hangmanden bij elkaar, borders apart, gazon apart en kas of moestuin apart. Noteer per zone hoeveel minuten water nodig is voordat de grond zichtbaar vochtig is.</p>
                <p>Controleer daarna drie punten die in echte tuinen vaak verschil maken: lekt de koppeling na tien minuten nog steeds niet, sluit de klep volledig wanneer je stopt, en blijft de laatste druppelaar of sproeier genoeg druk houden?</p>
              </section>`);
          }
        }
        articleRte.appendChild(wrap);
      }
    }
  });
})();
